/**
 * Guarda de permiso de las pantallas de Horario (semanal / láser).
 *
 * La ruta ya cuelga de `RequireAuth`; el gating de permiso lo hace la pantalla
 * (mismo patrón que `stats/RequireAdvancedStats`). Replica la paridad con el SSR
 * (`docs/frontend_architecture.md`, Pr. 5): el horario semanal exige `schedule` y el
 * láser exige `laser_schedule` (mismos permisos que el menú del SSR y las páginas);
 * sin el permiso se muestra un aviso en vez del contenido.
 *
 * Para el horario láser, la VISIBILIDAD de la entrada del menú depende además de
 * `sede_laser_activo` (lo gobierna `navConfig.ts`); aquí solo se comprueba el
 * permiso (igual que el SSR, cuya página `laser_schedule` solo exigía el permiso).
 */
import type { ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { useAuth } from "@/lib/auth";

interface RequireSchedulePermissionProps {
  /** Permiso exigido (`schedule` o `laser_schedule`). */
  permission: string;
  /** Título de la pantalla (para el aviso de carga / sin permiso). */
  title: string;
  children: ReactNode;
}

export function RequireSchedulePermission({
  permission,
  title,
  children,
}: RequireSchedulePermissionProps) {
  const { status, hasPermission } = useAuth();

  // Mientras la sesión se hidrata (`/auth/me`), `hasPermission` aún no conoce los
  // permisos: mostrar "sin permiso" en ese instante sería engañoso. Se espera a
  // tener la sesión resuelta (la ruta ya está protegida por `RequireAuth`).
  if (status === "loading") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500" role="status">
            Cargando…
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!hasPermission(permission)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600" role="alert">
            No tienes permiso para ver este horario.
          </p>
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
}
